import { SchemaFormatter } from '.'
import { type PaymentProvider } from './Payment'

/**
 * The type of search to perform
 * - Lax - Will use fuzzy search to find broaden the search
 * - Strict - Will only tolerate one letter typos (Default)
 * - Exact - Will only return exact matches
 */
export type SmartSearchType = 'Lax' | 'Strict' | 'Exact'
export const defaultSmartSearchType: SmartSearchType = 'Strict'

export type Currency = (typeof CurrencyList)[number]
export const CurrencyList = ['NOK'] as const
export enum CurrencyEnum {
  NOK = 'NOK',
}
export const CurrencyMap: Record<Currency, number> = {
  NOK: 1,
}

export const PaymentProviderList = [
  'Easy',
  'Stripe',
  'Dintero',
  'KidNorway',
  'SwedbankPay',
  'bank2bank',
] as const as PaymentProvider[]

export const ConnectedAccountTypeList = [
  'unknown',
  'individual',
  'municipality',
  'LLC',
  'organization',
  'company',
  'enkeltmannsforetak',
  'aksjeselskap',
  'forening',
  'kommune',
] as const

export enum ComplianceStatusEnum {
  Green = 'Green',
  Yellow = 'Yellow',
  Red = 'Red',
}
export const ComplianceStatus = [
  ComplianceStatusEnum.Green,
  ComplianceStatusEnum.Yellow,
  ComplianceStatusEnum.Red,
] as const
export const ConnectedAccountComplianceStatus = ComplianceStatus

export const proffDataBusinessTypeMap = {
  ENK: 'enkeltmannsforetak',
  AS: 'aksjeselskap',
  FLI: 'forening',
  KOMM: 'kommune',
}

export const ConnectedAccountTypesTranslated = {
  unknown: 'Ukjent',
  enkeltmannsforetak: 'Enkeltmannsforetak',
  kommune: 'Kommune',
  aksjeselskap: 'Aksjeselskap',
  forening: 'Forening',
}

const PaymentProviderListToEnum: Record<(typeof PaymentProviderList)[number], number> = {
  Easy: 2,
  Stripe: 6,
  Dintero: 8,
  KidNorway: 10,
  SwedbankPay: 12,
  bank2bank: 14,
}
const EnvironmentToEnum: Record<PaymentEnvironment[number], number> = {
  production: 1,
  test: 2,
}

export const PaymentStatuses = [
  'InitiatedPayment',
  'ReservedPayment',
  'ChargedPayment',
  'RefundedPayment',
  'CancelledPayment',
  'FailedPayment',
  'RefundInitiated',
  'InvoiceConfirmed',
] as const

export const PaymentEnvironmentList = ['production', 'test'] as const
export type PaymentEnvironment = (typeof PaymentEnvironmentList)[number]
export const PaymentEnvironmentEnum = {
  production: 1,
  test: 0,
}

export const ConnectedAccountLedgerTypeMap = {
  Unknown: 0,
  MoneySplit: 1,
  FeePaidByPlatformToPejer: 2,
  PejerFeeProfitReceivedFromPlatform: 3,
  RefundSplit: 4,
  RefundFee: 5,
  PartialRefundSplit: 6,

  // different types of processor fee's are listed from 10 and above
  PaymentProcessorFee: 10,

  PaymentProcessorStripeCharge: 20,

  PaymentProcessorKidNorwayCharge: 30,
  PaymentProcessorKidNorwayRefund: 31,

  PaymentProcessorSwedbankCharge: 40,

  // different types fee's paid to Pejer are listed from 1000 and above

  /// <summary>
  /// fee on payouts from the receiver of the money. Caller is the CreatePayout method in the Payout module
  /// Typically 500 cents and it is charged to the receiver of the money.
  /// caller: the Payout module
  /// </summary>
  DirectRemittanceToPejer: 1000,

  /// <summary>
  /// fee paid by the platform to Pejer for OCR payments. Caller is the SFTP service/ OCRHandler
  /// typically 225 cents and it is charged from the platform
  /// caller: OCRHandler which is runned by the SFTP service
  /// </summary>
  OCRPaymentFee: 1001,

  /// <summary>
  /// fee paid by the platform to Pejer for creating a new invoice in KidNorway
  /// typically 75 cents and it is charged from the platform
  /// Caller: CreatePaymentIntent with payment provider "invoice"
  /// </summary>
  KidNorwayCreateNewInvoiceFee: 1002,

  /// <summary>
  /// fee paid by the platform to Pejer for refunding an OCR payment
  /// typically 300 cents and it is charged from the platform
  /// caller: none at the moment
  /// </summary>
  OCRPaymentFeeRefund: 1003,
}

export const CustomerTypeList = ['Unknown', 'Individual', 'Business'] as const
export enum CustomerTypeEnum {
  Unknown = 'Unknown',
  Individual = 'Individual',
  Business = 'Business',
}
export type CustomerType = (typeof PaymentEnvironmentList)[number]

export const PaymentStatusMap: Record<(typeof PaymentStatuses)[number], number> = {
  InitiatedPayment: 1,
  ReservedPayment: 2,
  ChargedPayment: 3,

  RefundedPayment: 4,

  CancelledPayment: 5,
  FailedPayment: 6,
  RefundInitiated: 7,
  InvoiceConfirmed: 8,
}

export const MoneyFormatter: SchemaFormatter<any, any> = {
  from: (value) => (value === null ? null : value / 100),
  to: (value) => (value === null ? null : value * 100),
}

export const ProviderFormatter: SchemaFormatter<string> = {
  from: (value) => value, //getPaymentProviderData(Number(value)).name,
  to: (value) => value, //String(PaymentProviderListToEnum[value as PaymentProvider])
}

export const PaymentEnvironmentFormatter: SchemaFormatter<any, any> = basicEnumFormatter(
  PaymentEnvironmentList,
  EnvironmentToEnum,
  true
)

export const PaymentStatusFormatter: SchemaFormatter<any, any> = basicEnumFormatter(
  PaymentStatuses,
  PaymentStatusMap,
  true
)

// {
//   from: (value) => PaymentEnvironmentList[Number(value)],
//   to: (value) => String(EnvironmentToEnum[value as PaymentProvider])
// }

function basicEnumFormatter(
  list: readonly string[],
  enumObject: Record<string, number>,
  ignore_from = false
): SchemaFormatter<string> {
  return {
    from: (value) => {
      return value
      const val = list[Number(value)]

      if (!val) throw new Error('FROM FORMATTER - Unknown enum value: ' + value)
      return val
    },
    to: (value) => {
      return value
      const val = enumObject[value]
      if (!val) throw new Error('TO FORMATTER - Unknown enum value: ' + value)
      return String(val)
    },
  }
}
